import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem,
  Grid, Alert, CircularProgress, Autocomplete, Box, Typography, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, Chip, IconButton
} from '@mui/material';
import {
  Search as SearchIcon, CheckCircle as SelectIcon, Inventory as InventoryIcon,
  Science as ScienceIcon, Add as AddIcon, Delete as DeleteIcon
} from '@mui/icons-material';
import axios from 'axios';

const API_BASE_URL = 'https://ccc4cs.choicegroup.in/api';

const PURPOSES = ['QC Inspection', 'QC Sample', 'Kitchen', 'Other'];

// Generate FCL Month options (current month + 11 previous months + 3 future months)
const generateFclMonthOptions = () => {
  const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
  const options = [];
  const today = new Date();
  
  // Start from 3 months in the future, go back 14 months
  for (let i = 3; i >= -14; i--) {
    const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
    const monthStr = months[d.getMonth()];
    const yearStr = d.getFullYear();
    options.push(`${monthStr}-${yearStr}`);
  }
  
  return options;
};

function AddQCSampleModal({ open, onClose, onSuccess, filterOptions }) {
  const [searchFilters, setSearchFilters] = useState({
    product: '', grade: '', brand: '', trace_id: '', day_code: '', pallet_id: ''
  });
  const [pallets, setPallets] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [selectedSamples, setSelectedSamples] = useState([]);
  const [showFinalForm, setShowFinalForm] = useState(false);
  const [formData, setFormData] = useState({
    sample_date: new Date().toISOString().split('T')[0],
    fcl_month: '', fcl_no: '', purpose: 'QC Inspection', other_reason: '', notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successInfo, setSuccessInfo] = useState(null);

  // Generate FCL Month options
  const fclMonthOptions = useMemo(() => generateFclMonthOptions(), []);

  useEffect(() => {
    if (open) {
      setSearchFilters({ product: '', grade: '', brand: '', trace_id: '', day_code: '', pallet_id: '' });
      setPallets([]);
      setSelectedSamples([]);
      setShowFinalForm(false);
      setFormData({
        sample_date: new Date().toISOString().split('T')[0],
        fcl_month: '', fcl_no: '', purpose: 'QC Inspection', other_reason: '', notes: ''
      });
      setError('');
      setSearchError('');
      setSuccessInfo(null);
    }
  }, [open]);

  const handleSearch = async () => {
    const hasFilter = Object.values(searchFilters).some(v => v.trim() !== '');
    if (!hasFilter) { setSearchError('Please enter at least one search criteria'); return; }

    setSearching(true);
    setSearchError('');
    setPallets([]);

    try {
      const params = new URLSearchParams({ action: 'search_pallets' });
      Object.entries(searchFilters).forEach(([key, value]) => {
        if (value.trim()) params.append(key, value.trim());
      });

      const response = await axios.get(`${API_BASE_URL}/qc_samples.php?${params.toString()}`, { withCredentials: true });

      if (response.data.success) {
        setPallets(response.data.data || []);
        if (response.data.data.length === 0) setSearchError('No pallets found matching your criteria');
      } else {
        setSearchError(response.data.message || 'Search failed');
      }
    } catch (err) {
      setSearchError('Error searching pallets. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  const handleFilterChange = (name, value) => {
    setSearchFilters(prev => ({ ...prev, [name]: value || '' }));
  };

  const handleAddSample = (pallet) => {
    if (selectedSamples.find(s => s.pallet.id === pallet.id)) return;
    setSelectedSamples(prev => [...prev, { pallet, sample_bags: '' }]);
  };

  const handleRemoveSample = (palletId) => {
    setSelectedSamples(prev => prev.filter(s => s.pallet.id !== palletId));
  };

  const handleSampleBagsChange = (palletId, value) => {
    setSelectedSamples(prev => prev.map(s => 
      s.pallet.id === palletId ? { ...s, sample_bags: value } : s
    ));
  };

  const handleProceedToFinal = () => {
    const valid = selectedSamples.every(s => {
      const bags = parseInt(s.sample_bags);
      return bags > 0 && bags <= s.pallet.total_available_bags;
    });
    if (!valid) { setError('Please enter valid sample bags for all selected pallets'); return; }
    setShowFinalForm(true);
    setError('');
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async () => {
    if (!formData.fcl_month || !formData.fcl_no) {
      setError('FCL Month and FCL Number are required'); return;
    }
    if (formData.purpose === 'Other' && !formData.other_reason.trim()) {
      setError('Please provide reason for "Other" purpose'); return;
    }

    setLoading(true);
    setError('');

    try {
      const samples = selectedSamples.map(s => ({
        inventory_id: s.pallet.id,
        sample_bags: parseInt(s.sample_bags)
      }));

      const response = await axios.post(`${API_BASE_URL}/qc_samples.php`, {
        action: 'create_multiple',
        samples,
        sample_date: formData.sample_date,
        fcl_month: formData.fcl_month,
        fcl_no: formData.fcl_no,
        purpose: formData.purpose === 'Other' ? formData.other_reason : formData.purpose,
        notes: formData.notes
      }, { headers: { 'Content-Type': 'application/json' }, withCredentials: true });

      if (response.data.success) {
        setSuccessInfo(response.data);
        setTimeout(() => { onSuccess(); handleClose(); }, 2500);
      } else {
        setError(response.data.message || 'Failed to create QC samples');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error creating QC samples');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => { if (!loading) onClose(); };

  const getTotalSampleBags = () => selectedSamples.reduce((sum, s) => sum + (parseInt(s.sample_bags) || 0), 0);

  const getStatusColor = (status) => ({
    'PRODUCED': { bg: '#4caf50', text: 'white' },
    'REPACK-IN': { bg: '#ff9800', text: 'white' },
    'RE-PRINTING': { bg: '#2196f3', text: 'white' },
    'RE-PROCESS': { bg: '#9c27b0', text: 'white' }
  }[status] || { bg: '#757575', text: 'white' });

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth PaperProps={{ sx: { minHeight: '70vh' } }}>
      <DialogTitle sx={{ 
        background: 'linear-gradient(135deg, #d32f2f 0%, #1565c0 100%)', 
        background: 'linear-gradient(135deg, #d32f2f 0%, #1565c0 100%)',
        color: 'white', 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        py: 2
      }}>
        <ScienceIcon sx={{ fontSize: 28 }} /> 
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Add QC Sample {selectedSamples.length > 0 && `(${selectedSamples.length} pallets selected)`}
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ mt: 2, bgcolor: '#fafafa' }}>
        {successInfo && (
          <Alert severity="success" sx={{ mb: 2, '& .MuiAlert-message': { fontWeight: 600 } }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>✓ {successInfo.message}</Typography>
            {successInfo.details?.map((d, i) => (
              <Typography key={i} variant="body2" sx={{ mt: 0.5 }}>
                Pallet <strong>{d.pallet_id}</strong>: {d.sample_bags} bags → {d.after.cartons} cartons, {d.after.loose_bags} loose
              </Typography>
            ))}
          </Alert>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {!showFinalForm ? (
          <>
            {/* Search Section */}
            <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2, border: '1px solid #e0e0e0' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1565c0', display: 'flex', alignItems: 'center', gap: 1 }}>
                <SearchIcon /> Step 1: Search Inventory
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6} md={4} lg={2}>
                  <Autocomplete
                    freeSolo
                    options={filterOptions?.products || []}
                    value={searchFilters.product}
                    onChange={(e, v) => handleFilterChange('product', v)}
                    onInputChange={(e, v, r) => r === 'input' && handleFilterChange('product', v)}
                    renderInput={(params) => (
                      <TextField {...params} label="Product" placeholder="e.g., CPND" variant="outlined" 
                        sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'white' } }} />
                    )}
                    ListboxProps={{ style: { maxHeight: 300 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                  <Autocomplete
                    freeSolo
                    options={filterOptions?.grades || []}
                    value={searchFilters.grade}
                    onChange={(e, v) => handleFilterChange('grade', v)}
                    onInputChange={(e, v, r) => r === 'input' && handleFilterChange('grade', v)}
                    renderInput={(params) => (
                      <TextField {...params} label="Grade" placeholder="e.g., 31/40" variant="outlined"
                        sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'white' } }} />
                    )}
                    ListboxProps={{ style: { maxHeight: 300 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                  <Autocomplete
                    freeSolo
                    options={filterOptions?.brands || []}
                    value={searchFilters.brand}
                    onChange={(e, v) => handleFilterChange('brand', v)}
                    onInputChange={(e, v, r) => r === 'input' && handleFilterChange('brand', v)}
                    renderInput={(params) => (
                      <TextField {...params} label="Brand" placeholder="e.g., TC HEB" variant="outlined"
                        sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'white' } }} />
                    )}
                    ListboxProps={{ style: { maxHeight: 300 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                  <Autocomplete
                    freeSolo
                    options={filterOptions?.trace_ids || []}
                    value={searchFilters.trace_id}
                    onChange={(e, v) => handleFilterChange('trace_id', v)}
                    onInputChange={(e, v, r) => r === 'input' && handleFilterChange('trace_id', v)}
                    renderInput={(params) => (
                      <TextField {...params} label="Trace ID" placeholder="e.g., AP043528" variant="outlined"
                        sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'white' } }} />
                    )}
                    componentsProps={{
                      popper: { style: { width: 'auto', minWidth: 250 } }
                    }}
                    ListboxProps={{ style: { maxHeight: 300 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                  <Autocomplete
                    freeSolo
                    options={filterOptions?.day_codes || []}
                    value={searchFilters.day_code}
                    onChange={(e, v) => handleFilterChange('day_code', v)}
                    onInputChange={(e, v, r) => r === 'input' && handleFilterChange('day_code', v)}
                    renderInput={(params) => (
                      <TextField {...params} label="Day Code" placeholder="e.g., 5M18" variant="outlined"
                        sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'white' } }} />
                    )}
                    componentsProps={{
                      popper: { style: { width: 'auto', minWidth: 150 } }
                    }}
                    ListboxProps={{ style: { maxHeight: 300 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleSearch}
                    disabled={searching}
                    startIcon={searching ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                    sx={{ 
                      height: '56px', 
                      fontWeight: 700, 
                      fontSize: '1rem',
                      background: 'linear-gradient(135deg, #d32f2f 0%, #1565c0 100%)',
                      '&:hover': { background: 'linear-gradient(135deg, #c62828 0%, #0d47a1 100%)' }
                    }}
                  >
                    SEARCH
                  </Button>
                </Grid>
              </Grid>
              {searchError && <Alert severity="warning" sx={{ mt: 2 }}>{searchError}</Alert>}
            </Paper>

            {/* Pallets Table */}
            {pallets.length > 0 && (
              <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2, border: '1px solid #e0e0e0' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1565c0', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <InventoryIcon /> Step 2: Select Pallets 
                  <Chip label={`${pallets.length} found`} color="primary" size="small" sx={{ ml: 1, fontWeight: 700 }} />
                </Typography>
                <TableContainer sx={{ maxHeight: 350, borderRadius: 1, border: '1px solid #e0e0e0' }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, bgcolor: '#1565c0', color: 'white', fontSize: '0.9rem' }}>Add</TableCell>
                        <TableCell sx={{ fontWeight: 700, bgcolor: '#1565c0', color: 'white', fontSize: '0.9rem' }}>Pallet ID</TableCell>
                        <TableCell sx={{ fontWeight: 700, bgcolor: '#1565c0', color: 'white', fontSize: '0.9rem' }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700, bgcolor: '#1565c0', color: 'white', fontSize: '0.9rem' }}>Product</TableCell>
                        <TableCell sx={{ fontWeight: 700, bgcolor: '#1565c0', color: 'white', fontSize: '0.9rem' }}>Grade</TableCell>
                        <TableCell sx={{ fontWeight: 700, bgcolor: '#1565c0', color: 'white', fontSize: '0.9rem' }}>Pack Size</TableCell>
                        <TableCell sx={{ fontWeight: 700, bgcolor: '#1565c0', color: 'white', fontSize: '0.9rem' }}>Brand</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, bgcolor: '#1565c0', color: 'white', fontSize: '0.9rem' }}>Cartons</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, bgcolor: '#1565c0', color: 'white', fontSize: '0.9rem' }}>Loose</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, bgcolor: '#2e7d32', color: 'white', fontSize: '0.9rem' }}>Total Avail.</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pallets.map((pallet, idx) => {
                        const isSelected = selectedSamples.find(s => s.pallet.id === pallet.id);
                        const statusColors = getStatusColor(pallet.status);
                        return (
                          <TableRow 
                            key={pallet.id} 
                            hover 
                            sx={{ 
                              bgcolor: isSelected ? '#e3f2fd' : (idx % 2 === 0 ? '#ffffff' : '#f5f5f5'),
                              '&:hover': { bgcolor: isSelected ? '#bbdefb' : '#e3f2fd' },
                              transition: 'all 0.2s'
                            }}
                          >
                            <TableCell>
                              <IconButton 
                                size="small" 
                                onClick={() => !isSelected && handleAddSample(pallet)} 
                                disabled={isSelected}
                                sx={{ 
                                  bgcolor: isSelected ? '#4caf50' : '#1565c0', 
                                  color: 'white',
                                  '&:hover': { bgcolor: isSelected ? '#4caf50' : '#1565c0' },
                                  '&.Mui-disabled': { bgcolor: '#4caf50', color: 'white' }
                                }}
                              >
                                {isSelected ? <SelectIcon /> : <AddIcon />}
                              </IconButton>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '1rem', color: '#1565c0' }}>{pallet.pallet_id}</TableCell>
                            <TableCell>
                              <Chip 
                                label={pallet.status} 
                                size="small" 
                                sx={{ bgcolor: statusColors.bg, color: statusColors.text, fontWeight: 700, fontSize: '0.75rem' }} 
                              />
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#333' }}>{pallet.product}</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#333' }}>{pallet.grade}</TableCell>
                            <TableCell>
                              <Chip label={pallet.pack_size} size="small" sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 700 }} />
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#333' }}>{pallet.brand}</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700, fontSize: '1rem', color: '#1565c0' }}>{pallet.cartons}</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700, fontSize: '1rem', color: '#ff9800' }}>{pallet.loose_bags}</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#2e7d32', bgcolor: '#e8f5e9' }}>
                              {pallet.total_available_bags}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}

            {/* Selected Samples */}
            {selectedSamples.length > 0 && (
              <Paper elevation={3} sx={{ p: 3, borderRadius: 2, bgcolor: '#e8f5e9', border: '2px solid #4caf50' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#2e7d32', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ScienceIcon /> Step 3: Enter Sample Bags
                  <Chip label={`${selectedSamples.length} selected`} sx={{ ml: 1, bgcolor: '#4caf50', color: 'white', fontWeight: 700 }} />
                </Typography>
                <TableContainer sx={{ borderRadius: 1, bgcolor: 'white' }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#4caf50' }}>
                        <TableCell sx={{ fontWeight: 700, color: 'white' }}>Remove</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'white' }}>Pallet ID</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'white' }}>Product</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'white' }}>Grade</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'white' }}>Pack Size</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, color: 'white' }}>Available</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, color: 'white', bgcolor: '#ff9800' }}>Sample Bags *</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'white' }}>Result Preview</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedSamples.map(({ pallet, sample_bags }, idx) => {
                        const bags = parseInt(sample_bags) || 0;
                        let newCartons = pallet.cartons, newLoose = pallet.loose_bags, remaining = bags;
                        if (newLoose >= remaining) { newLoose -= remaining; remaining = 0; }
                        else { remaining -= newLoose; newLoose = 0; }
                        if (remaining > 0 && pallet.bags_per_carton > 0) {
                          const toBreak = Math.ceil(remaining / pallet.bags_per_carton);
                          newCartons -= toBreak;
                          newLoose = (toBreak * pallet.bags_per_carton) - remaining;
                        }
                        return (
                          <TableRow key={pallet.id} sx={{ bgcolor: idx % 2 === 0 ? '#f5f5f5' : 'white' }}>
                            <TableCell>
                              <IconButton size="small" onClick={() => handleRemoveSample(pallet.id)}
                                sx={{ bgcolor: '#f44336', color: 'white', '&:hover': { bgcolor: '#d32f2f' } }}>
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '1rem', color: '#1565c0' }}>{pallet.pallet_id}</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>{pallet.product}</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>{pallet.grade}</TableCell>
                            <TableCell>
                              <Chip label={pallet.pack_size} size="small" sx={{ bgcolor: '#e3f2fd', fontWeight: 600 }} />
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700, color: '#1565c0', fontSize: '1rem' }}>
                              {pallet.total_available_bags}
                            </TableCell>
                            <TableCell align="center">
                              <TextField 
                                size="small" 
                                type="number" 
                                value={sample_bags}
                                onChange={(e) => handleSampleBagsChange(pallet.id, e.target.value)}
                                inputProps={{ min: 1, max: pallet.total_available_bags, style: { textAlign: 'center', fontWeight: 700 } }}
                                error={bags > pallet.total_available_bags}
                                sx={{ width: 100, bgcolor: 'white' }}
                              />
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: bags > 0 ? '#2e7d32' : '#999' }}>
                              {bags > 0 ? (
                                <Box>
                                  <span style={{ color: '#1565c0' }}>{newCartons}</span> ctns + 
                                  <span style={{ color: '#ff9800', marginLeft: 4 }}>{newLoose}</span> loose
                                </Box>
                              ) : '-'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box sx={{ mt: 2, p: 2, bgcolor: '#c8e6c9', borderRadius: 1, textAlign: 'right' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                    Total Sample Bags: <span style={{ fontSize: '1.5rem', color: '#1565c0' }}>{getTotalSampleBags()}</span>
                  </Typography>
                </Box>
              </Paper>
            )}
          </>
        ) : (
          /* Final Form - FCL Details */
          <Paper elevation={3} sx={{ p: 4, borderRadius: 2, border: '2px solid #1565c0' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: '#1565c0', display: 'flex', alignItems: 'center', gap: 1 }}>
              📋 Step 4: Enter FCL Details
            </Typography>
            <Alert severity="info" sx={{ mb: 3, fontSize: '1rem' }}>
              <strong>Summary:</strong> Taking samples from <strong>{selectedSamples.length}</strong> pallet(s), 
              Total: <strong style={{ color: '#1565c0', fontSize: '1.2rem' }}>{getTotalSampleBags()}</strong> bags
            </Alert>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField fullWidth required type="date" label="Sample Date" name="sample_date"
                  value={formData.sample_date} onChange={handleFormChange} InputLabelProps={{ shrink: true }}
                  sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'white' } }} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField 
                  fullWidth 
                  required 
                  select 
                  label="FCL Month" 
                  name="fcl_month" 
                  value={formData.fcl_month} 
                  onChange={handleFormChange}
                  sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'white' } }}
                >
                  {fclMonthOptions.map(month => (
                    <MenuItem key={month} value={month}>{month}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField fullWidth required label="FCL Number" name="fcl_no" placeholder="e.g., 17"
                  value={formData.fcl_no} onChange={handleFormChange}
                  sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'white' } }} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField fullWidth select label="Purpose" name="purpose" value={formData.purpose} onChange={handleFormChange}
                  sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'white' } }}>
                  {PURPOSES.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                </TextField>
              </Grid>
              {formData.purpose === 'Other' && (
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth required label="Specify Reason" name="other_reason"
                    value={formData.other_reason} onChange={handleFormChange} placeholder="Enter reason..."
                    sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'white' } }} />
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField fullWidth multiline rows={2} label="Notes (Optional)" name="notes"
                  value={formData.notes} onChange={handleFormChange}
                  sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'white' } }} />
              </Grid>
            </Grid>
          </Paper>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: '#f5f5f5', borderTop: '1px solid #e0e0e0' }}>
        {showFinalForm && (
          <Button onClick={() => setShowFinalForm(false)} disabled={loading} variant="outlined" sx={{ mr: 1 }}>
            ← Back
          </Button>
        )}
        <Button onClick={handleClose} disabled={loading} variant="outlined" color="inherit">
          Cancel
        </Button>
        {!showFinalForm ? (
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleProceedToFinal}
            disabled={selectedSamples.length === 0 || !selectedSamples.every(s => parseInt(s.sample_bags) > 0)}
            sx={{ 
              fontWeight: 700, 
              px: 4,
              background: 'linear-gradient(135deg, #d32f2f 0%, #1565c0 100%)',
            }}
          >
            Proceed to FCL Details →
          </Button>
        ) : (
          <Button 
            variant="contained" 
            onClick={handleSubmit} 
            disabled={loading || successInfo}
            startIcon={loading && <CircularProgress size={20} color="inherit" />}
            sx={{ 
              fontWeight: 700, 
              px: 4,
              background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
              '&:hover': { background: 'linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)' }
            }}
          >
            {loading ? 'Creating...' : '✓ Create QC Samples'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default AddQCSampleModal;